/**
 * Tenant Context Provider
 *
 * Provides centralized tenant context management with:
 * - Token lifecycle management (auto-refresh when claims missing)
 * - Tenant metadata loading from Firestore
 * - Explicit loading/error states
 * - Defense-in-depth validation
 *
 * Architecture:
 * 1. User Authentication (Firebase Auth)
 * 2. Tenant Context Provider (validates claims, loads metadata)
 * 3. Application Components (consume context)
 *
 * Best Practices:
 * - Firebase custom claims require token refresh to be available
 * - Explicit state management prevents race conditions
 * - Error classification enables better UX
 */

import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { doc, getDoc, db } from '@/lib/firestoreClient';
import { useAuth } from './AuthContext';
import type { TenantData } from './useTenant';

/**
 * Tenant context state
 */
export interface TenantContextState {
  /** Current tenant ID from custom claims */
  tenantId: string | null;
  /** Tenant metadata from Firestore */
  tenantData: TenantData | null;
  /** Loading state (true during initial load or refresh) */
  loading: boolean;
  /** Error state (set when claims missing or Firestore fails) */
  error: Error | null;
  /** Manually refresh tenant context (useful after tenant updates) */
  refreshTenant: () => Promise<void>;
}

/**
 * Error codes for tenant context
 */
export enum TenantContextError {
  /** Custom claims missing from token (not yet set or token not refreshed) */
  CLAIM_MISSING = 'TENANT_CLAIM_MISSING',
  /** Tenant document not found in Firestore */
  NOT_FOUND = 'TENANT_NOT_FOUND',
  /** Firestore permission denied */
  PERMISSION_DENIED = 'PERMISSION_DENIED',
  /** Network or other error */
  UNKNOWN = 'UNKNOWN_ERROR',
}

const TenantContext = createContext<TenantContextState | undefined>(undefined);

/**
 * Tenant Context Provider
 *
 * Wraps the application to provide tenant context.
 * Should be placed inside AuthProvider in the component tree.
 *
 * @example
 * ```tsx
 * <AuthProvider>
 *   <TenantProvider>
 *     <App />
 *   </TenantProvider>
 * </AuthProvider>
 * ```
 */
export function TenantProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [tenantId, setTenantId] = useState<string | null>(null);
  const [tenantData, setTenantData] = useState<TenantData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  /**
   * Load tenant context from Firebase Auth claims and Firestore
   *
   * CRITICAL: Custom claims are NOT available until token is refreshed.
   * This function:
   * 1. Checks if tenantId claim exists
   * 2. If missing, forces token refresh and retries
   * 3. Loads tenant metadata from Firestore
   * 4. Sets error state if tenant not found or claims missing
   */
  async function loadTenantContext() {
    if (!user) {
      setTenantId(null);
      setTenantData(null);
      setLoading(false);
      setError(null);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Step 1: Get ID token and check for tenantId claim
      const token = await user.getIdTokenResult();
      let claimedTenantId = token.claims.tenantId as string | undefined;

      // Step 2: If claim missing, force token refresh
      if (!claimedTenantId) {
        console.warn('[TenantContext] No tenantId claim found - forcing token refresh');

        // Force token refresh (true = force)
        await user.getIdToken(true);
        const refreshedToken = await user.getIdTokenResult();
        claimedTenantId = refreshedToken.claims.tenantId as string | undefined;

        // If still missing after refresh, user has no tenant
        // This is NORMAL during registration flow - don't throw error
        if (!claimedTenantId) {
          console.warn('[TenantContext] No tenantId claim after refresh - user may be in registration flow');
          setTenantId(null);
          setTenantData(null);
          setError(null);
          setLoading(false);
          return;
        }
      }

      // Step 3: Load tenant metadata from Firestore
      const tenantDocRef = doc(db, 'tenants', claimedTenantId);
      const tenantDocSnap = await getDoc(tenantDocRef);

      if (!tenantDocSnap.exists()) {
        const err = new Error('Organization data not found. Please contact support.');
        err.name = TenantContextError.NOT_FOUND;
        throw err;
      }

      // Step 4: Extract tenant data
      const data = tenantDocSnap.data();
      const tenantResult: TenantData = {
        id: tenantDocSnap.id,
        companyName: data.companyName ?? data.name ?? 'My Company',
        countryCode: data.countryCode ?? 'DE',
        ownerUid: data.ownerUid ?? '',
        branding: data.branding,
        domain: data.domain,
        address: data.address,
        timezone: data.timezone,
        currency: data.currency,
        workingDays: data.workingDays,
        workDayStartTime: data.workDayStartTime,
        workDayEndTime: data.workDayEndTime,
        annualLeaveDays: data.annualLeaveDays,
        sickLeaveDays: data.sickLeaveDays,
        carryOverDays: data.carryOverDays,
        fiscalYearStart: data.fiscalYearStart,
      };

      setTenantId(claimedTenantId);
      setTenantData(tenantResult);
      setError(null);
    } catch (err) {
      console.error('[TenantContext] Error loading tenant context:', err);

      // Classify error for better UX
      if (err instanceof Error) {
        // Check for Firestore permission denied
        if (err.message.includes('permission-denied') || err.message.includes('PERMISSION_DENIED')) {
          err.name = TenantContextError.PERMISSION_DENIED;
          err.message = 'Access denied. Please ensure you are logged in with the correct account.';
        }
      }

      setError(err as Error);
      setTenantId(null);
      setTenantData(null);
    } finally {
      setLoading(false);
    }
  }

  /**
   * Manually refresh tenant context
   * Useful after:
   * - Tenant data updated
   * - User switches tenants (future feature)
   * - Custom claims updated
   */
  async function refreshTenant() {
    await loadTenantContext();
  }

  // Load tenant context when user changes
  useEffect(() => {
    void loadTenantContext();
  }, [user?.uid]); // Only re-run when user ID changes, not on every user object change

  const contextValue: TenantContextState = {
    tenantId,
    tenantData,
    loading,
    error,
    refreshTenant,
  };

  return <TenantContext.Provider value={contextValue}>{children}</TenantContext.Provider>;
}

/**
 * Hook to access tenant context
 *
 * @throws Error if used outside TenantProvider
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { tenantId, tenantData, loading, error } = useTenantContext();
 *
 *   if (loading) return <Spinner />;
 *   if (error) return <ErrorMessage error={error} />;
 *   if (!tenantId) return <NoTenantMessage />;
 *
 *   return <div>Welcome to {tenantData?.companyName}</div>;
 * }
 * ```
 */
export function useTenantContext(): TenantContextState {
  const context = useContext(TenantContext);

  if (context === undefined) {
    throw new Error('useTenantContext must be used within a TenantProvider');
  }

  return context;
}

/**
 * Optional variant that returns null instead of throwing when outside TenantProvider.
 * Use in components that may render before the provider is ready (e.g., breadcrumbs, layout).
 */
export function useTenantContextOptional(): TenantContextState | null {
  return useContext(TenantContext) ?? null;
}
